import { User, LocationCode, Clinic } from "pwapi";

import { JwtResponse } from "custom-models";

import {
  LOGIN_FAILURE,
  LOGIN_SUCCESS,
  TOKEN_FAILURE,
  TOKEN_RECEIVED,
  DEAUTHENTICATE,
  RESET_AUTH_STATE,
  SET_PROFILE_SUCCESS,
  RESET_AUTH_ERRORS,
  MFA_VERIFY_CODE_SUCCESS,
  MFA_SELECT_METHOD,
  MFA_GET_CODE_SUCCESS,
  MFA_GET_CODE_REQUEST,
  MFA_VERIFY_CODE_FAILURE,
  USER_DETAIL_SUCCESS,
  USER_ME_SUCCESS,
  USER_UNSET_MFA_METHOD_SUCCESS,
} from "../actions/constants";

export interface Jwt {
  user_id: number;
  exp: number;
  iat: number;
  jti: string;
  token_type: "access" | "refresh";
  impersonator_id?: number;
}

export interface AuthState {
  access?: Jwt;
  refresh?: Jwt;

  // longLivedToken is used for services that access the frontend
  // i.e. the PDF generator
  longLivedToken?: string;

  errors: any;
  user?: User & {
    // TODO: because this comes back from the "MeSerializer",
    // OpenAPI does not include it in the ApiService; it just thinks it's a "User"
    location_codes?: LocationCode[];
    clinics?: Clinic[];
  };
  mfa_method?: "sms" | "email" | "app";
  mfa_app_uri?: string;
  mfa_code_required: boolean;
  mfa_setup_required: boolean;
  has_email: boolean;
  has_phone: boolean;
  versions: JwtResponse["versions"];
}

export const initialState: AuthState = {
  access: undefined,
  refresh: undefined,
  longLivedToken: undefined,
  errors: {},
  user: undefined,
  mfa_method: undefined,
  mfa_app_uri: undefined,
  mfa_code_required: false,
  mfa_setup_required: false,
  has_email: false,
  has_phone: false,
  versions: {},
};

function reduceFailure(state: AuthState, action: any) {
  return {
    ...state,
    errors: action.payload.body || {
      non_field_errors: action.payload.statusText || action.payload.message,
      detail: action.payload.statusText || action.payload.message,
    },
  };
}

const reducer = (state: AuthState = initialState, action: any): AuthState => {
  switch (action.type) {
    case LOGIN_SUCCESS:
      return {
        ...initialState,
        ...action.payload,
      };
    case TOKEN_RECEIVED:
      return {
        ...state,
        ...action.payload,
      };
    case DEAUTHENTICATE:
      return initialState;
    case RESET_AUTH_STATE:
      return initialState;
    case LOGIN_FAILURE:
      if (action.payload?.body?.mfa_code_required)
        return {
          ...state,
          mfa_code_required: true,
          has_email: action.payload?.body?.has_email,
          has_phone: action.payload?.body?.has_phone,
          mfa_method: action.payload?.body?.mfa_method,
          mfa_app_uri: action.payload?.body?.mfa_app_uri,
        };
      return reduceFailure(state, action);
    case TOKEN_FAILURE:
      return reduceFailure(state, action);
    case SET_PROFILE_SUCCESS:
      return {
        ...state,
        user: action.payload,
      };
    case USER_ME_SUCCESS:
      return {
        ...state,
        user: action.payload.response,
      };
    case USER_DETAIL_SUCCESS:
      if (state.user?.id && action.payload.id === state.user.id) {
        return {
          ...state,
          user: action.payload,
        };
      }
      return state;
    case USER_UNSET_MFA_METHOD_SUCCESS:
      if (state.user?.id && action.payload.response.id === state.user.id) {
        return {
          ...state,
          user: action.payload.response,
        };
      }
      return state;
    case RESET_AUTH_ERRORS:
      return {
        ...state,
        errors: {},
      };
    case MFA_GET_CODE_REQUEST:
      return {
        ...state,
        mfa_app_uri: undefined,
      };
    case MFA_GET_CODE_SUCCESS:
      return {
        ...state,
        mfa_app_uri: action.payload.mfa_app_uri,
      };
    case MFA_VERIFY_CODE_SUCCESS:
      return {
        ...state,
        // @ts-ignore
        user: {
          ...state.user,
          userprofile: {
            ...state.user?.userprofile,
            mfa_method: action.payload.mfa_method,
          },
        },
        mfa_setup_required: false,
        mfa_app_uri: undefined,
      };
    case MFA_VERIFY_CODE_FAILURE:
      return reduceFailure(state, action);
    case MFA_SELECT_METHOD:
      return {
        ...state,
        mfa_method: action.payload,
        mfa_app_uri: undefined,
      };
    default:
      return state;
  }
};

export default reducer;

export const selectAuth = ({ auth }: { auth: AuthState }) => auth;

// Returns true when access token or refresh token are expired.
export function isAccessTokenExpired(state: AuthState) {
  if (state?.longLivedToken) return false;
  if (!state.access?.exp) return true;

  const expiredAccess = 1000 * state.access.exp <= Date.now();
  const expiredRefresh =
    state.refresh?.exp && 1000 * state.refresh.exp <= Date.now();
  return expiredAccess || expiredRefresh;
}

export function isRefreshTokenExpired(state: AuthState) {
  if (state?.longLivedToken) return false;
  if (!state.refresh?.exp) return true;

  return state.refresh?.exp && 1000 * state.refresh.exp <= Date.now();
}

export const selectIsAccessTokenExpired = ({ auth }: { auth: AuthState }) =>
  isAccessTokenExpired(auth);
export const selectIsRefreshTokenExpired = ({ auth }: { auth: AuthState }) =>
  isRefreshTokenExpired(auth);

export function errors(state: any) {
  return state.errors;
}

export const selectMfaSetupRequired = ({ auth }: { auth: AuthState }) =>
  auth.mfa_setup_required;

export const selectLongLivedToken = ({ auth }: { auth: AuthState }) =>
  auth.longLivedToken;
